import { Box, Switch, SwitchProps, Theme, useTheme } from '@mui/material'
import useStyles from '~/common/hooks/useStyles'

export interface DossSwitchProps extends Omit<SwitchProps, 'color' | 'size'> {
  color?: string
  size?: 'sm' | 'md' | 'lg'
}

function getBaseStyles(theme: Theme) {
  return {
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: theme.spacing(0.25),
      transitionDuration: '100ms',
      transition: 'transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: theme.palette.common.white,
        '& + .MuiSwitch-track': {
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        border: `6px solid ${theme.palette.common.white}`,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        content: '"On"',
        opacity: 0.7,
      },
    },
    '& .MuiSwitch-track': {
      backgroundColor: theme.palette.common.doss.gray[3],
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 200,
      }),
    },
  }
}

function getColorStyles(color: string, theme: Theme) {
  return {
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: color,
    },
    '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.primary.main,
    },
  }
}

function getSizeStyles(size: 'sm' | 'md' | 'lg') {
  const sizes = {
    sm: { width: 28, height: 16, thumbSize: 12, transform: 'translateX(12px)' },
    md: { width: 35, height: 21, thumbSize: 17, transform: 'translateX(14px)' },
    lg: { width: 42, height: 26, thumbSize: 22, transform: 'translateX(16px)' },
  }
  const { width, height, thumbSize, transform } = sizes[size]
  return {
    width,
    height,
    '& .MuiSwitch-switchBase': {
      '&.Mui-checked': {
        transform,
      },
    },
    '& .MuiSwitch-thumb': {
      width: thumbSize,
      height: thumbSize,
    },
    '& .MuiSwitch-track': {
      borderRadius: height / 2,
    },
  }
}

function getStyles(theme: Theme) {
  return {
    dossSwitch: (color: string, size: 'sm' | 'md' | 'lg' = 'md') => ({
      ...getBaseStyles(theme),
      ...getColorStyles(color, theme),
      ...getSizeStyles(size),
    }),
  }
}

function DossSwitch(props: DossSwitchProps) {
  const theme = useTheme()
  const { color = theme.palette.primary.main, size = 'md', ...rest } = props
  const styles = useStyles(getStyles)

  return (
    <Box>
      <Switch
        disableRipple
        focusVisibleClassName="Mui-focusVisible"
        sx={styles.dossSwitch(color, size)}
        {...rest}
      />
    </Box>
  )
}

export default DossSwitch
